﻿import { ConfiguratorDI, Enumerable, Kbo } from "@models/configurators/configurator-di";
import { CToken } from "@models/configurators/ctoken";
import { IDummyNode } from "@models/configurators/kb-object";
import { KbObjectManager } from "@models/configurators/kb-object-manager";
import { OutputField } from "@models/configurators/output-fields/output-field";
import { CadOutput } from "@models/configurators/outputs/cad-output";
import { RuleContainer } from "@models/configurators/rule-container";
import { IOutput, IOutputField, ITag } from "@models/entity-models";
import { eExportType, eOutputFieldType, eOutputType, eRuleType } from "@models/enums";
import { Export } from "./export";

export interface IExportType {
    extension: string;
    name: string;
}

@Kbo(CToken.Output)
export class Output extends RuleContainer implements IOutput {
    constructor(kom: KbObjectManager, args?: IOutput) {
        super(kom);
        this.ruleType = eRuleType.output;
        if (args) {
            kom.recurse(args, this);
        }
        this.$manager.addKbObject(this, undefined);
        this.$fieldsNode = { id: this.id + "_fields", $type: "FieldsNode", allowedChildren: () => [CToken.OutputField], getParent: () => this };
        this.$rulesNode = { id: this.id + "_rules", $type: "RulesNode", allowedChildren: () => [CToken.Rule], getParent: () => this };
        this.$exportsNode = { id: this.id + "_exports", $type: "ExportsNode", allowedChildren: () => [CToken.Export], getParent: () => this };
        this.$drawingsNode = { id: this.id + "_drawings", $type: "DrawingsNode", allowedChildren: () => [CToken.CadDrawingOutput], getParent: () => this };
        this.ruleType = eRuleType.output;
    }

    public static isCadOutput(outputType: string): boolean {
        return outputType == eOutputType.inventor ||
            outputType == eOutputType.solidworks ||
            outputType == eOutputType.creo ||
            outputType == eOutputType.autoCad;
    }

    public static supportsCadDrawings(outputType: string): boolean {
        return outputType == eOutputType.inventor ||
            outputType == eOutputType.solidworks ||
            outputType == eOutputType.creo;
    }
    
    public static getOutputExportTypes(output: Output): IExportType[] {
        if (!output) return [];

        let et = (extension: string, name: string) => {
            return this.createExportType(extension, name);
        };

        switch (output.type) {
            case eOutputType.word:
                return [
                    et(eExportType.pdf, "Adobe Portable Document Format"),
                    et(eExportType.bmp, "Bitmap"),
                    et(eExportType.doc, "Word Document 97-2003"),
                    et(eExportType.docm, "Macro-Enabled Word Document"),
                    et(eExportType.dot, "Word Template 97-2003"),
                    et(eExportType.dotm, "Macro-Enabled Word Template 97-2003"),
                    et(eExportType.dotx, "Word Template"),
                    et(eExportType.emf, "EMF"),
                    et(eExportType.epub, "EPUB"),
                    et(eExportType.html, "HTML"),
                    et(eExportType.jpg, "JPEG"),
                    et(eExportType.png, "PNG"),
                    et(eExportType.rtf, "Rich Text Format"),
                    et(eExportType.txt, "Text"),
                    et(eExportType.tiff, "TIFF"),
                    et(eExportType.xaml, "XAML"),
                    et(eExportType.xps, "XPS"),
                    et(eExportType.odt, "Open Document Text")
                ];
            case eOutputType.excel:
                return [
                    et(eExportType.pdf, "Adobe Portable Document Format"),
                    et(eExportType.csv, "CSV File"),
                    et(eExportType.xml, "Xml"),
                    et(eExportType.html, "HTML"),
                    et(eExportType.ods, "Open Document Spreadsheet"),
                    et(eExportType.svg, "SVG File"),
                    et(eExportType.txt, "Tab Delimited Text File"),
                    et(eExportType.tiff, "TIFF"),
                    et(eExportType.xps, "XPS")
                ];
            case eOutputType.creo:
                return [
                    et(eExportType.x_t, "Parasolid"),
                    et(eExportType.igs, "IGES"),
                    et(eExportType.step, "STEP"),
                    et(eExportType.sat, "ACIS"),
                    et(eExportType.stl, "STL"),
                    et(eExportType.wrl, "VRML"),
                    et(eExportType.vda, "VDA"),
                    et(eExportType.pdf, "Adobe Portable Document Format"),
                    et(eExportType.eps, "eps"),
                    et(eExportType.neu, "Neutral"),
                    et(eExportType.jpg, "JPEG"),
                    et(eExportType.tif, "Tif"),
                    et(eExportType.iv, "Inventor"),
                    et(eExportType.slp, "Render")
                ];
            case eOutputType.inventor:
                return [
                    et(eExportType.jpg, "JPEG"),
                    et(eExportType.bmp, "BMP"),
                    et(eExportType.gif, "GIF"),
                    et(eExportType.png, "PNG"),
                    et(eExportType.tiff, "TIFF"),
                    et(eExportType.pdf, "Adobe Portable Document Format"),
                    et(eExportType.catproduct, "CATIA V5 Product File"),
                    et(eExportType.igs, "IGES"),
                    et(eExportType.jt, "JT"),
                    et(eExportType.x_b, "Parasolid Binary"),
                    et(eExportType.x_t, "Parasolid Text"),
                    et(eExportType.g, "Pro/Engineer Granite Files"),
                    et(eExportType.neu, "Pro/Engineer NEutral File"),
                    et(eExportType.sat, "SAT"),
                    et(eExportType.step, "STEP"),
                    et(eExportType.stl, "STL"),
                    et(eExportType.xgl, "XGL"),
                    et(eExportType.zgl, "ZGL"),
                    et(eExportType.dwf, "DWF")
                ];
            case eOutputType.solidworks:
                return [
                    et(eExportType.sldprt, "Solidworks Part"),
                    et(eExportType.x_t, "Parasolid"),
                    et(eExportType.x_b, "Parasolid Binary"),
                    et(eExportType.igs, "IGES"),
                    et(eExportType.step, "STEP"),
                    et(eExportType.sat, "ACIS"),
                    et(eExportType.stl, "STL"),
                    et(eExportType.wrl, "VRML"),
                    et(eExportType.easm, "eDrawings Assembly"),
                    et(eExportType.pdf, "Adobe Portable Document Format"),
                    et(eExportType.u3d, "Universal 3D"),
                    et(eExportType.psd, "Adobe Photoshop File"),
                    et(eExportType.ai, "Adobe Illustrator File"),
                    et(eExportType.xaml, "Microsoft XAML"),
                    et(eExportType.cgr, "Catia Graphics"),
                    et(eExportType.asm, "ProE Assembly"),
                    et(eExportType.jpg, "JPEG"),
                    et(eExportType.hcg, "HCG"),
                    et(eExportType.hsf, "HOOPS HSF"),
                    et(eExportType.tif, "Tif")
                ];
            case eOutputType.autoCad:
                return [
                    et(eExportType.dxf, "DXF"),
                    et(eExportType.pdf, "PDF"),
                    // et(eExportType.dgn, "DGN"),
                    et(eExportType.dwf, "DWF"),
                    et(eExportType.bmp, "BMP"),
                    et(eExportType.stl, "STL"),
                    et(eExportType.igs, "IGS")
                ];
            case eOutputType.cadDrawing:
                return Output.getCadDrawingExportTypes(output);
            case eOutputType.bom:
                return [
                    et(eExportType.xml, "XML"),
                    et(eExportType.txt, "Text")
                ];
            case eOutputType.svg:
                return [
                    et(eExportType.jpg, "JPEG"),
                    et(eExportType.bmp, "BMP"),
                    et(eExportType.gif, "GIF"),
                    et(eExportType.png, "PNG"),
                    et(eExportType.pdf, "PDF")
                ]
            case eOutputType.sceneModel:
                return [
                    et(eExportType.glb, "GLB"),
                    // et(eExportType.fbx, "FBX"),
                ]
            case eOutputType.text:
                return [
                    et(eExportType._custom, "Custom File Extension")
                ]
        }
        return [];
    }

    private static getCadDrawingExportTypes(output: Output): IExportType[] {
        let et = (extension: string, name: string) => {
            return this.createExportType(extension, name);
        };

        switch ((output.$parent as Output).type) {
            case eOutputType.solidworks:
                return [
                    et(eExportType.dxf, "DXF"),
                    et(eExportType.dwg, "DWG"),
                    et(eExportType.psd, "Adobe Photoshop File"),
                    et(eExportType.ai, "Adobe Illustrator File"),
                    et(eExportType.pdf, "Adobe Portable Document Format"),
                    et(eExportType.edrw, "EDrawings"),
                    et(eExportType.jpg, "JPEG"),
                    et(eExportType.tif, "Tif")
                ];
            case eOutputType.inventor:
                return [
                    et(eExportType.jpg, "JPEG"),
                    et(eExportType.bmp, "BMP"),
                    et(eExportType.gif, "GIF"),
                    et(eExportType.png, "PNG"),
                    et(eExportType.tiff, "TIFF"),
                    et(eExportType.pdf, "PDF"),
                    et(eExportType.dwg, "DWG"),
                    et(eExportType.dwf, "DWF")
                ];
            case eOutputType.creo:
                return [
                    et(eExportType.dwg, "DWG"),
                    et(eExportType.dxf, "DXF"),
                    et(eExportType.pdf, "Adobe Portable Document Format"),
                    et(eExportType.tiff, "TIFF")
                ];
        }
        return [];
    }

    private static createExportType(extension: string, name: string): IExportType {
        return {
            extension,
            name: name + " (." + extension + ")"
        };
    }

    public static getOutputFieldTypes(output: IOutput) {
        if (!output) return [];
        return Output.getOutputFieldTypesByOutputType(output.type);
    }

    public static getOutputFieldTypesByOutputType(outputType) {
        switch (outputType) {
            case eOutputType.word:
                return [
                    eOutputFieldType.wordText,
                    eOutputFieldType.wordImage,
                    eOutputFieldType.wordContainer,
                    eOutputFieldType.wordList];
            case eOutputType.excel:
                return [
                    eOutputFieldType.excelCell,
                    eOutputFieldType.excelImage,
                    eOutputFieldType.excelSheet,
                    eOutputFieldType.excelTable,
                    eOutputFieldType.excelChart];
            case eOutputType.creo:
            case eOutputType.solidworks:
                return [
                    eOutputFieldType.cadAnnotation,
                    eOutputFieldType.cadComponent,
                    eOutputFieldType.cadCustomProperty,
                    eOutputFieldType.cadDimension,
                    eOutputFieldType.cadDynamicComponent,
                    eOutputFieldType.cadFeature,
                    eOutputFieldType.cadLibFeature,
                    eOutputFieldType.cadReplaceComponent,
                    eOutputFieldType.cadScaleFeature];
            case eOutputType.autoCad:
                return [
                    eOutputFieldType.cadLayer,
                    eOutputFieldType.cadBlock,
                    eOutputFieldType.cadBlockArray,
                    eOutputFieldType.cadDimension,
                    eOutputFieldType.cadText];
            case eOutputType.inventor:
                return [
                    eOutputFieldType.cadAnnotation,
                    eOutputFieldType.cadComponent,
                    eOutputFieldType.cadCustomProperty,
                    eOutputFieldType.cadDimension,
                    eOutputFieldType.cadDynamicComponent,
                    eOutputFieldType.cadFeature,
                    eOutputFieldType.cadLibFeature,
                    eOutputFieldType.cadReplaceComponent,
                    eOutputFieldType.cadScaleFeature,
                    eOutputFieldType.cadParameter];
            case eOutputType.cadDrawing:
                return [
                    eOutputFieldType.cadDrawingAnnotation,
                    eOutputFieldType.cadDrawingDimension,
                    eOutputFieldType.cadDynamicDrawingDimension,
                    eOutputFieldType.cadDynamicDrawingAnnotation,
                    eOutputFieldType.cadDrawingSheet,
                    eOutputFieldType.cadDrawingView,
                    eOutputFieldType.cadCustomProperty,
                    eOutputFieldType.cadLayer];
            case eOutputType.pdf:
                return [
                    eOutputFieldType.pdfSection
                ];
            case eOutputType.zip:
                return [
                    eOutputFieldType.zipSection
                ];
        }
        return [];
    }

    public static getConstraintFieldTypes() {
        return [
            eOutputFieldType.cadDistanceMate,
            eOutputFieldType.cadAngleMate,
            eOutputFieldType.cadCoordinateSystemMate,
            eOutputFieldType.cadCoincidentMate,
            eOutputFieldType.cadPositionConstraint
        ];
    }

    public static getLibFeatureConstraintTypes() {
        return [
            eOutputFieldType.cadLibDimension,
            eOutputFieldType.cadLibReference
        ];
    }

    public static usesTemplate(outputType: string) {
        switch (outputType) {
            case eOutputType.solidworks:
            case eOutputType.inventor:
            case eOutputType.creo:
            case eOutputType.cadDrawing:
            case eOutputType.word:
            case eOutputType.excel:
            case eOutputType.autoCad:
                return true;
        }

        return false;
    }

    private _fields: OutputField[];
    @Enumerable()
    public get fields(): OutputField[] {
        if (!this._fields) this._fields = [];
        return this._fields;
    }
    public set fields(val) {
        this._fields = val;
    }

    private _exports: Export[];
    @Enumerable()
    public get exports(): Export[] {
        if (!this._exports) this._exports = [];
        return this._exports;
    }
    public set exports(val) {
        this._exports = val;
    }

    private _tags: ITag[];
    @Enumerable()
    public get tags(): ITag[] {
        if (!this._tags) this._tags = [];
        return this._tags;
    }
    public set tags(val) {
        this._tags = val;
    }

    public enabled: boolean;
    public resultFilePath: string;

    public $fieldsNode: IDummyNode;
    public $rulesNode: IDummyNode;
    public $exportsNode: IDummyNode;
    public $drawingsNode: IDummyNode;
    public type: string;
    public autoValidation: boolean;
    public overwritePreExistingBuilds: boolean;

    public addExport(type: string): Export {
        let exprt = new Export(this.$manager);
        exprt.type = type;
        exprt.name = this.type == eOutputType.sceneRender ? "Render" : exprt.type + " Export";
        this.$manager.addKbObject(exprt, this);
        this.exports.push(exprt);
        return exprt;
    }

    public removeExport(exp: Export) {
        this.$manager.removeKbObject(exp);
        this.exports.remove(exp);
    }

    public addOutputField(fieldType: eOutputFieldType, args?: IOutputField): OutputField {
        let field = ConfiguratorDI.getNewOutputField(fieldType, this.$manager, args);
        this.$manager.addKbObject(field, this);
        this.fields.push(field);
        return field;
    }

    public removeOutputField(outputField: OutputField) {
        this.$manager.removeKbObject(outputField);
        this.fields.remove(outputField);
    }

    public allRuleContainers(): RuleContainer[] {
        let containers: RuleContainer[] = [this];
        if (ConfiguratorDI.instanceOf<CadOutput>(this, CToken.CadOutput)) {
            containers.concat(this.drawings);
        }
        return containers;
    }
}
